import { resizeImage } from "../../../helpers/ImageHelper";
import { Method, ROUTES } from "../../configuration/Configuration";
import { fetchAPI } from "../../configuration/MainService";
import { S3URLDto } from "../../dto/S3URL.Dto";

export class WSMedia {
  private static instance: WSMedia;

  private constructor() { }

  static getInstance(): WSMedia {
    if (!WSMedia.instance) {
      WSMedia.instance = new WSMedia();
    }
    return WSMedia.instance;
  }

  async getGeneratedURL(): Promise<string> {
    try {
      const url = `${ROUTES.MEDIA}${ROUTES.GET_S3_URL}`;

      const response = await fetchAPI<S3URLDto>({
        url,
        method: Method.GET,
        params: {},
        isAuthenticated: true,
      });
      return response.data.uploadURL;
    } catch (error) {
      console.error("Get generated URL failed:", error);
      throw error;
    }
  }

  async saveImage(
    s3Url: string,
    file: File,
    isResized: boolean = true,
  ): Promise<string> {
    try {
      let resizedBlob: Blob | File;
      if (!isResized) {
        resizedBlob = file;
      } else {
        resizedBlob = await resizeImage(file, 800, 800);
      }
      const resizedFile = new File([resizedBlob], file.name, {
        type: file.type,
        lastModified: Date.now(),
      });
      const response = await fetchAPI({
        url: s3Url,
        method: Method.PUT,
        headers: {
          "Content-Type": resizedFile.type || "application/octet-stream",
        },
        params: resizedFile,
        isFile: true,
      });

      return s3Url.split("?")[0];
    } catch (error) {
      console.error("Image processing/upload failed:", error);
      throw error;
    }
  }


  async saveFileWithProgress(
    s3Url: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", s3Url);

      xhr.setRequestHeader("Content-Type", "application/vnd.android.package-archive");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(s3Url.split("?")[0]);
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during S3 upload"));

      xhr.send(file);
    });
  }

  async deleteImage(keys: string[]): Promise<void> {
    try {
      const url = `${ROUTES.MEDIA}${ROUTES.DELETE}`;
      const extractS3Key = keys.map((key) => this.extractS3Key(key));

      await fetchAPI({
        url,
        method: Method.POST,
        params: extractS3Key,
        isAuthenticated: true,
      });
    } catch (error: any) {
      console.error("Get generated URL failed:", error);
      throw error;
    }
  }

  private extractS3Key(url: string): string | null {
    try {
      // Handle both http and https URLs
      const urlObj = new URL(url);
      // Remove the leading slash from pathname and return
      return urlObj.pathname.substring(1);
    } catch (e) {
      console.error("Invalid URL provided:", e);
      return null;
    }
  }
}
